import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchCfdiBackendPreflight,
  fetchEfirmaProfileStatus,
  fetchCfdiRequestStatus,
  getCfdiIntegrationConfigError,
  getEfirmaControlConfigError,
  getEfirmaIntegrationConfigError,
  isEfirmaControlConfigured,
  isCfdiIntegrationConfigured,
  isEfirmaStatusConfigured,
  sendEfirmaControlRequest,
  sendCfdiSecureRequest
} from '../../services/n8n';
import {
  MAX_CERT_FILE_SIZE_BYTES,
  buildEfirmaControlPayload,
  buildCfdiSecurePayload,
  buildSatRequestSemantics,
  computeSha256Hex,
  fileToBase64,
  isValidSha256Hex,
  normalizeCfdiRunStatus,
  normalizeEfirmaProfile,
  resolveBackendErrorMessage,
  buildSubmitErrorMessage,
  validateCertificateFile,
  validateCfdiRequestForm
} from '../../utils/cfdiRequest';

const MAX_FILE_MB = Math.round(MAX_CERT_FILE_SIZE_BYTES / (1024 * 1024));
const AUTO_STATUS_POLL_INTERVAL_MS = 10000;
const TERMINAL_CFDI_STATUS = new Set(['completada', 'terminada', 'rechazada']);

const isTerminalCfdiStatus = status =>
  TERMINAL_CFDI_STATUS.has(String(status || '').trim().toLowerCase());

const normalizeClientList = clientDirectory => {
  const list = Object.values(clientDirectory || {})
    .filter(client => Boolean(client) && client.isActive !== false)
    .map(client => ({
      ...client,
      label: [client.id, client.empresa, client.idCliente, client.rfc].filter(Boolean).join(' · ')
    }))
    .sort((a, b) => (a.label || '').localeCompare(b.label || '', 'es-MX'));

  return list;
};

const getFileHealth = info => {
  if (info?.error) {
    return { tone: 'danger', label: 'Requiere atencion' };
  }
  if (info?.file && info?.hash) {
    return { tone: 'success', label: 'Validado localmente' };
  }
  return { tone: 'neutral', label: 'Pendiente' };
};

const getStatusTone = status => {
  const normalized = String(status || '').trim().toLowerCase();
  if (!normalized) return 'neutral';
  if (['aceptada', 'completada', 'terminada'].includes(normalized)) return 'success';
  if (['pendiente', 'verificando', 'enviada'].includes(normalized)) return 'warning';
  if (['rechazada'].includes(normalized)) return 'danger';
  return 'neutral';
};

const getStatusLabel = status => {
  const normalized = String(status || '').trim().toLowerCase();
  if (!normalized) return 'Sin ejecucion';
  if (normalized === 'pendiente') return 'Pendiente de SAT';
  if (normalized === 'verificando') return 'En verificacion';
  if (normalized === 'enviada') return 'Enviada al backend';
  if (normalized === 'aceptada') return 'Aceptada';
  if (normalized === 'completada') return 'Completada';
  if (normalized === 'terminada') return 'Terminada con paquetes';
  if (normalized === 'rechazada') return 'Rechazada';
  return normalized.replace(/_/g, ' ');
};

const getPackageHashLabel = item => {
  const algorithm = String(item?.algorithm || '').trim().toUpperCase() || 'HASH';
  const hash = String(item?.hash || '').trim();
  if (!hash) return '';
  const compactHash = hash.length > 16 ? `${hash.slice(0, 12)}...${hash.slice(-4)}` : hash;
  return `${algorithm}: ${compactHash}`;
};

const getRequestDeliverableLabel = requestType => {
  const normalized = String(requestType || '').trim().toUpperCase();
  if (normalized === 'CFDI') {
    return 'paquetes ZIP para CFDI';
  }
  if (normalized === 'METADATA') {
    return 'archivo de metadatos para control y conciliacion';
  }
  if (normalized === 'PDF') {
    return 'paquete con representaciones PDF';
  }
  if (normalized === 'TXTUUIDMASIVA') {
    return 'archivo TXT con UUIDs';
  }
  return 'archivo entregado por SAT';
};

const getOriginLabel = runStatus => {
  const downloadFiles = Array.isArray(runStatus?.downloadFiles) ? runStatus.downloadFiles : [];
  if (runStatus.isAuthoritative) {
    return 'SAT real';
  }
  if (['SAT_BACKEND_NOT_READY', 'PREFLIGHT_UNAVAILABLE'].includes(String(runStatus.errorCode || '').trim())) {
    return 'bloqueado';
  }
  if (runStatus.mirrorInfo?.origin === 'INTERNAL_MIRROR' || downloadFiles.some(file => file.mirrorInfo?.origin === 'INTERNAL_MIRROR')) {
    return 'copia interna';
  }
  return 'sin validar';
};

const getOriginTone = runStatus => {
  const originLabel = getOriginLabel(runStatus);
  if (originLabel === 'SAT real') return 'success';
  if (originLabel === 'bloqueado') return 'danger';
  if (originLabel === 'copia interna') return 'warning';
  return 'neutral';
};

const createEmptyCertState = () => ({
  file: null,
  hash: '',
  base64: '',
  error: ''
});

const buildCfdiUserMessage = normalizedStatus => {
  if (normalizedStatus.errorCode) {
    return '';
  }
  const packageCount = normalizedStatus.idsPaquetes.length;
  const downloadFileCount = Array.isArray(normalizedStatus.downloadFiles) ? normalizedStatus.downloadFiles.length : 0;
  const satId = normalizedStatus.idSolicitudSat || 'sin folio SAT';
  const deliverable = getRequestDeliverableLabel(normalizedStatus.requestType);
  const originLabel = getOriginLabel(normalizedStatus);
  if (!normalizedStatus.isAuthoritative) {
    return `Solicitud registrada. Folio ${satId}. Estado ${getStatusLabel(normalizedStatus.estadoSolicitud)}. Origen del dato: ${originLabel}.`;
  }
  if (normalizedStatus.requestType === 'CFDI' && downloadFileCount > 0) {
    if (normalizedStatus.estadoSolicitud === 'terminada') {
      return `Proceso CFDI terminado. Folio SAT ${satId}. Se recibieron ${downloadFileCount} archivo${downloadFileCount === 1 ? '' : 's'} ZIP para descarga.`;
    }
    return `Solicitud CFDI enviada. Folio SAT ${satId}; estado actual: ${getStatusLabel(normalizedStatus.estadoSolicitud)}. Se recibieron ${downloadFileCount} archivo${downloadFileCount === 1 ? '' : 's'} ZIP para descarga.`;
  }
  if (normalizedStatus.estadoSolicitud === 'terminada') {
    return `Proceso CFDI terminado. Folio SAT ${satId} con ${packageCount} paquete${packageCount === 1 ? '' : 's'} listo${packageCount === 1 ? '' : 's'} para ${deliverable}.`;
  }
  return `Solicitud CFDI enviada. Folio SAT ${satId}; estado actual: ${getStatusLabel(normalizedStatus.estadoSolicitud)}. Entregable esperado: ${deliverable}.`;
};

const buildCfdiSummary = runStatus => {
  if (!runStatus.requestId) {
    return 'Cuando envíes una solicitud, aquí verás el folio SAT, los paquetes listos para descarga y los archivos de respaldo.';
  }
  if (runStatus.errorCode) {
    return `La solicitud se registró, pero requiere atención: ${runStatus.errorMessage || runStatus.errorCode}.`;
  }
  const packageCount = runStatus.idsPaquetes.length;
  const downloadFileCount = runStatus.downloadFiles.length;
  const deliverable = getRequestDeliverableLabel(runStatus.requestType);
  if (!runStatus.isAuthoritative) {
    return `La solicitud está en estado ${getStatusLabel(runStatus.estadoSolicitud).toLowerCase()} con origen ${getOriginLabel(runStatus)}. No se presenta contenido autoritativo del SAT.`;
  }
  const base = runStatus.requestType === 'CFDI'
    ? runStatus.estadoSolicitud === 'terminada'
      ? `La descarga masiva terminó correctamente. SAT reportó ${packageCount} paquete${packageCount === 1 ? '' : 's'} y se recibieron ${downloadFileCount} archivo${downloadFileCount === 1 ? '' : 's'} ZIP.`
      : `La solicitud está en estado ${getStatusLabel(runStatus.estadoSolicitud).toLowerCase()}. SAT ha reportado ${packageCount} paquete${packageCount === 1 ? '' : 's'} y hay ${downloadFileCount} archivo${downloadFileCount === 1 ? '' : 's'} ZIP listo${downloadFileCount === 1 ? '' : 's'} o esperado${downloadFileCount === 1 ? '' : 's'} para entrega administrativa.`
    : runStatus.estadoSolicitud === 'terminada'
      ? `La descarga masiva terminó correctamente y SAT devolvió ${packageCount} paquete${packageCount === 1 ? '' : 's'} para ${deliverable}.`
      : `La solicitud está en estado ${getStatusLabel(runStatus.estadoSolicitud).toLowerCase()}.`;
  if (runStatus.evidenceUploaded && runStatus.metadataFolderUrl) {
    return `${base} También se guardó un respaldo operativo en Drive para seguimiento administrativo.`;
  }
  return base;
};

const formatPrimaryDownloadLabel = fileName => {
  const normalized = String(fileName || '').trim();
  if (!normalized) return 'ZIP de XML listo';
  return normalized;
};

const getEfirmaTone = availability => {
  if (['vigente', 'disponible'].includes(availability)) return 'success';
  if (['por_vencer'].includes(availability)) return 'warning';
  if (['vencida', 'incompleta'].includes(availability)) return 'danger';
  return 'neutral';
};

const formatDateTime = value => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  try {
    return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short' }).format(parsed);
  } catch {
    return parsed.toLocaleString();
  }
};

function CfdiSecureRequestBoard({ clientDirectory = {}, requestedBy = '' }) {
  const clients = useMemo(() => normalizeClientList(clientDirectory), [clientDirectory]);
  const integrationConfigError = getCfdiIntegrationConfigError();
  const integrationReady = isCfdiIntegrationConfigured();
  const efirmaConfigError = getEfirmaIntegrationConfigError();
  const efirmaControlConfigError = getEfirmaControlConfigError();
  const efirmaControlReady = isEfirmaControlConfigured();
  const efirmaStatusReady = isEfirmaStatusConfigured();
  const [selectedClientId, setSelectedClientId] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [requestMode, setRequestMode] = useState('emitidos');
  const [requestType, setRequestType] = useState('CFDI');
  const [fechaInicial, setFechaInicial] = useState('');
  const [fechaFinal, setFechaFinal] = useState('');
  const [folio, setFolio] = useState('');
  const [passphraseRef, setPassphraseRef] = useState('');
  const [cerInfo, setCerInfo] = useState(createEmptyCertState());
  const [keyInfo, setKeyInfo] = useState(createEmptyCertState());
  const [submitError, setSubmitError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [autoStatusPollEnabled, setAutoStatusPollEnabled] = useState(false);
  const [backendStatus, setBackendStatus] = useState({
    checked: false,
    ready: false,
    backend: '',
    strictRealOnly: false,
    blockers: [],
    reason: ''
  });
  const [efirmaLoading, setEfirmaLoading] = useState(false);
  const [efirmaSyncing, setEfirmaSyncing] = useState(false);
  const [efirmaError, setEfirmaError] = useState('');
  const [efirmaMessage, setEfirmaMessage] = useState('');
  const [runStatus, setRunStatus] = useState({
    requestId: '',
    clientRecordId: '',
    idSolicitudSat: '',
    requestMode: '',
    requestType: '',
    downloadType: '',
    estadoSolicitud: '',
    idsPaquetes: [],
    packageHashes: [],
    satBackend: '',
    satDataOrigin: '',
    isAuthoritative: false,
    sourceTrace: null,
    mirrorInfo: { origin: '', mirrorOfSatPackage: false, isAuthoritative: false },
    errorCode: '',
    errorMessage: '',
    rawEstadoSolicitud: '',
    rawErrorMessage: '',
    clientName: '',
    clientFolderId: '',
    clientFolderUrl: '',
    metadataFolderId: '',
    metadataFolderUrl: '',
    evidenceFileId: '',
    evidenceFileName: '',
    evidenceFileUrl: '',
    evidenceUploaded: false,
    downloadFiles: [],
    runAt: '',
    finalizedAt: ''
  });
  const [efirmaProfile, setEfirmaProfile] = useState(() =>
    normalizeEfirmaProfile({})
  );
  const [reuseStoredFiles, setReuseStoredFiles] = useState(true);
  const [efirmaSyncRequired, setEfirmaSyncRequired] = useState(false);
  const passphraseRefValue = useRef(passphraseRef);
  const runStatusRef = useRef(runStatus);
  const statusLoadingRef = useRef(statusLoading);

  const selectedClient = selectedClientId ? clientDirectory[selectedClientId] : null;
  const cerHealth = getFileHealth(cerInfo);
  const keyHealth = getFileHealth(keyInfo);
  const statusTone = getStatusTone(runStatus.estadoSolicitud);
  const statusLabel = getStatusLabel(runStatus.estadoSolicitud);
  const runtimeHostname =
    typeof window !== 'undefined' && window.location
      ? String(window.location.hostname || '').trim().toLowerCase()
      : '';
  const preflightUnavailable = backendStatus.checked && Array.isArray(backendStatus.blockers)
    ? backendStatus.blockers.some(blocker => String(blocker?.id || '').toUpperCase() === 'PREFLIGHT_UNAVAILABLE')
    : false;
  const signerProbeOnlyBlocked =
    runtimeHostname === 'dev.ci.acnegocios.mx' &&
    backendStatus.checked &&
    String(backendStatus.backend || '').trim().toLowerCase() === 'real' &&
    Array.isArray(backendStatus.blockers) &&
    backendStatus.blockers.length > 0 &&
    backendStatus.blockers.every(
      blocker => String(blocker?.id || '').trim().toLowerCase() === 'signer_probe'
    );
  const backendBlocked =
    backendStatus.checked &&
    !signerProbeOnlyBlocked &&
    (preflightUnavailable || (backendStatus.strictRealOnly && !backendStatus.ready));
  const backendBlockCode = preflightUnavailable ? 'PREFLIGHT_UNAVAILABLE' : 'SAT_BACKEND_NOT_READY';
  const backendBlockMessage = backendBlocked
    ? String(
        backendStatus.blockers?.[0]?.summary ||
          (preflightUnavailable
            ? 'No fue posible validar preflight SAT para este entorno.'
            : 'Backend SAT real no disponible para esta operación.')
      )
    : '';
  const canCheckStatus = Boolean(runStatus.requestId) && integrationReady && !backendBlocked && !statusLoading;
  const canToggleAutoStatusPoll = Boolean(runStatus.requestId) && integrationReady && !backendBlocked;
  const efirmaTone = getEfirmaTone(efirmaProfile.availability);
  const hasLocalCertUploads = Boolean(cerInfo.file || keyInfo.file);
  const shouldReuseExisting = reuseStoredFiles && efirmaProfile.isReusable && !hasLocalCertUploads;
  const efirmaAvailabilityLabel = efirmaProfile.availability.replace(/_/g, ' ');
  const efirmaReuseChecklist = [
    {
      key: 'cerHash',
      label: 'CER hash',
      ok: Boolean(efirmaProfile?.reuseChecklist?.hasCerHash),
      value: efirmaProfile?.cerSha256 || ''
    },
    {
      key: 'keyHash',
      label: 'KEY hash',
      ok: Boolean(efirmaProfile?.reuseChecklist?.hasKeyHash),
      value: efirmaProfile?.keySha256 || ''
    },
    {
      key: 'cerAssetId',
      label: 'cer asset id',
      ok: Boolean(efirmaProfile?.reuseChecklist?.hasCerAssetId),
      value: efirmaProfile?.existingCerAssetId || ''
    },
    {
      key: 'keyAssetId',
      label: 'key asset id',
      ok: Boolean(efirmaProfile?.reuseChecklist?.hasKeyAssetId),
      value: efirmaProfile?.existingKeyAssetId || ''
    },
    {
      key: 'passphraseRef',
      label: 'passphraseRef',
      ok: Boolean(efirmaProfile?.reuseChecklist?.hasPassphraseRef),
      value: efirmaProfile?.passphraseRef || ''
    }
  ];
  const certsReady =
    Boolean(cerInfo.file) &&
    Boolean(keyInfo.file) &&
    !cerInfo.error &&
    !keyInfo.error &&
    isValidSha256Hex(cerInfo.hash) &&
    isValidSha256Hex(keyInfo.hash);
  const filteredClients = useMemo(() => {
    const normalizedSearch = String(clientSearch || '').trim().toLowerCase();
    if (!normalizedSearch) return clients;
    return clients.filter(client =>
      [client.idCliente, client.empresa, client.alias, client.nombreComercial, client.name, client.rfc, client.id]
        .filter(Boolean)
        .some(value => String(value).toLowerCase().includes(normalizedSearch))
    );
  }, [clientSearch, clients]);

  useEffect(() => {
    passphraseRefValue.current = passphraseRef;
  }, [passphraseRef]);

  useEffect(() => {
    runStatusRef.current = runStatus;
  }, [runStatus]);

  useEffect(() => {
    statusLoadingRef.current = statusLoading;
  }, [statusLoading]);

  useEffect(() => {
    let ignore = false;

    async function loadBackendStatus() {
      if (!integrationReady) {
        if (!ignore) {
          setBackendStatus({
            checked: false,
            ready: false,
            backend: '',
            strictRealOnly: false,
            blockers: [],
            reason: 'integration_not_ready'
          });
        }
        return;
      }

      try {
        const status = await fetchCfdiBackendPreflight({});
        if (ignore) return;
        setBackendStatus(status);
        if (status.checked && status.strictRealOnly && !status.ready) {
          setAutoStatusPollEnabled(false);
        }
      } catch (error) {
        if (ignore) return;
        setBackendStatus({
          checked: true,
          ready: false,
          backend: '',
          strictRealOnly: true,
          blockers: [
            {
              id: 'PREFLIGHT_UNAVAILABLE',
              summary: 'No fue posible validar el preflight SAT para este entorno.'
            }
          ],
          reason: error?.message || 'preflight_fetch_failed'
        });
      }
    }

    void loadBackendStatus();
    const intervalId = setInterval(() => {
      void loadBackendStatus();
    }, 30000);

    return () => {
      ignore = true;
      clearInterval(intervalId);
    };
  }, [integrationReady]);

  useEffect(() => {
    setEfirmaSyncRequired(false);
    setAutoStatusPollEnabled(false);
    clearLocalCertSelection();
    if (!selectedClientId) {
      setEfirmaProfile(normalizeEfirmaProfile({}));
      setEfirmaError('');
      setEfirmaMessage('');
      setReuseStoredFiles(true);
      return;
    }
    if (!efirmaControlReady && !efirmaStatusReady) {
      setEfirmaError(efirmaConfigError);
      setEfirmaProfile(normalizeEfirmaProfile({}));
      setReuseStoredFiles(false);
      return;
    }
    if (!efirmaStatusReady) {
      setEfirmaError('');
      setEfirmaProfile(normalizeEfirmaProfile({}));
      setReuseStoredFiles(false);
      setEfirmaLoading(false);
      return;
    }

    let ignore = false;
    async function loadEfirmaProfile() {
      setEfirmaLoading(true);
      setEfirmaError('');
      try {
        const profile = await fetchEfirmaProfileStatus({ clientRecordId: selectedClientId });
        if (ignore) return;
        setEfirmaProfile(profile);
        if (profile.passphraseRef && !passphraseRefValue.current) {
          setPassphraseRef(profile.passphraseRef);
        }
        setReuseStoredFiles(profile.isReusable);
      } catch (error) {
        if (ignore) return;
        setEfirmaProfile(normalizeEfirmaProfile({}));
        setEfirmaError(error.message || 'No fue posible consultar la eFirma almacenada.');
        setReuseStoredFiles(false);
      } finally {
        if (!ignore) {
          setEfirmaLoading(false);
        }
      }
    }

    loadEfirmaProfile();
    return () => {
      ignore = true;
    };
  }, [selectedClientId, efirmaControlReady, efirmaStatusReady, efirmaConfigError]);

  const handleCertFile = async (file, expectedExtension, setter) => {
    setter(createEmptyCertState());
    if (!file) return;
    setEfirmaSyncRequired(true);
    setReuseStoredFiles(false);
    const validationError = validateCertificateFile(file, expectedExtension);
    if (validationError) {
      setter({ file: null, hash: '', base64: '', error: validationError });
      return;
    }
    try {
      const [hash, base64] = await Promise.all([computeSha256Hex(file), fileToBase64(file)]);
      setter({ file, hash, base64, error: '' });
    } catch (error) {
      setter({
        ...createEmptyCertState(),
        error: error.message || 'No fue posible procesar el archivo.'
      });
    }
  };

  const clearLocalCertSelection = () => {
    setCerInfo(createEmptyCertState());
    setKeyInfo(createEmptyCertState());
  };

  const handleSyncEfirma = async () => {
    setEfirmaError('');
    setEfirmaMessage('');
    if (!selectedClient) {
      setEfirmaError('Selecciona un cliente antes de registrar la eFirma.');
      return;
    }
    if (!efirmaControlReady) {
      setEfirmaError(efirmaControlConfigError);
      return;
    }
    if (!String(passphraseRef || '').trim()) {
      setEfirmaError('Define passphraseRef antes de registrar la eFirma.');
      return;
    }
    if (!cerInfo.file || !keyInfo.file) {
      setEfirmaError('Carga ambos archivos para registrar o actualizar la eFirma.');
      return;
    }
    if (cerInfo.error || keyInfo.error) {
      setEfirmaError(cerInfo.error || keyInfo.error);
      return;
    }
    if (!isValidSha256Hex(cerInfo.hash) || !isValidSha256Hex(keyInfo.hash) || !cerInfo.base64 || !keyInfo.base64) {
      setEfirmaError('La eFirma no esta lista para registrarse. Vuelve a cargar los archivos.');
      return;
    }

    setEfirmaSyncing(true);
    try {
      const payload = buildEfirmaControlPayload({
        client: selectedClient,
        passphraseRef,
        cerFile: cerInfo.file,
        keyFile: keyInfo.file,
        cerHash: cerInfo.hash,
        keyHash: keyInfo.hash,
        cerBase64: cerInfo.base64,
        keyBase64: keyInfo.base64,
        requestedBy
      });
      console.info('[CFDI UI][eFirma] Registro solicitado', {
        clientRecordId: selectedClient?.id || '',
        requestedBy: requestedBy || '',
        cerFileName: cerInfo.file?.name || '',
        keyFileName: keyInfo.file?.name || '',
        efirmaStatusReady,
        efirmaControlReady,
        hasPassphraseRef: Boolean(String(passphraseRef || '').trim())
      });
      const profile = await sendEfirmaControlRequest({ payload });
      setEfirmaProfile(profile);
      setReuseStoredFiles(profile.isReusable);
      setEfirmaSyncRequired(false);
      if (profile.isReusable) {
        clearLocalCertSelection();
      }
      console.info('[CFDI UI][eFirma] Registro completado', {
        clientRecordId: selectedClient?.id || '',
        availability: profile?.availability || '',
        isReusable: Boolean(profile?.isReusable),
        hasCerAssetId: Boolean(String(profile?.existingCerAssetId || '').trim()),
        hasKeyAssetId: Boolean(String(profile?.existingKeyAssetId || '').trim()),
        hasCerHash: Boolean(String(profile?.cerSha256 || '').trim()),
        hasKeyHash: Boolean(String(profile?.keySha256 || '').trim()),
        drivePath: profile?.drivePath || '',
        message: profile?.message || ''
      });
      if (!profile.isReusable) {
        console.warn('[CFDI UI][eFirma] Registro exitoso, pero sin metadata suficiente para reuso', {
          clientRecordId: selectedClient?.id || '',
          availability: profile?.availability || '',
          hasCerAssetId: Boolean(String(profile?.existingCerAssetId || '').trim()),
          hasKeyAssetId: Boolean(String(profile?.existingKeyAssetId || '').trim()),
          hasCerHash: Boolean(String(profile?.cerSha256 || '').trim()),
          hasKeyHash: Boolean(String(profile?.keySha256 || '').trim()),
          message: profile?.message || ''
        });
      }
      setEfirmaMessage(
        profile.isReusable
          ? 'Material criptografico registrado y listo para reutilizarse en futuras descargas.'
          : 'Material criptografico registrado, pero falta metadata para habilitar reutilizacion segura.'
      );
    } catch (error) {
      console.error('[CFDI UI][eFirma] Registro fallo', {
        clientRecordId: selectedClient?.id || '',
        message: error?.message || '',
        code: error?.code || error?.errorCode || '',
        status: error?.status || '',
        payload: error?.payload || null
      });
      setEfirmaError(error.message || 'No fue posible registrar la eFirma.');
    } finally {
      setEfirmaSyncing(false);
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setSubmitError('');
    setStatusMessage('');
    setAutoStatusPollEnabled(false);

    const formError = validateCfdiRequestForm({
      clientRecordId: selectedClientId,
      mode: requestMode,
      requestType,
      passphraseRef,
      fechaInicial,
      fechaFinal,
      folio
    });
    if (formError) {
      setSubmitError(formError);
      return;
    }
    if (!integrationReady) {
      setSubmitError(integrationConfigError);
      return;
    }
    if (backendBlocked) {
      setSubmitError(`${backendBlockCode}: ${backendBlockMessage || 'Backend SAT real no disponible.'}`);
      return;
    }
    if (efirmaSyncing) {
      setSubmitError('Espera a que termine el registro o la actualizacion de la eFirma antes de enviar la solicitud.');
      return;
    }
    if (efirmaSyncRequired) {
      setSubmitError('Registra o actualiza la eFirma antes de enviar la solicitud CFDI.');
      return;
    }

    let profileForSubmission = efirmaProfile;
    const wantsReuseWithoutLocalUploads = reuseStoredFiles && !hasLocalCertUploads;
    if (efirmaStatusReady && selectedClientId) {
      try {
        const refreshedProfile = await fetchEfirmaProfileStatus({ clientRecordId: selectedClientId });
        const canReuseLastKnownProfile =
          wantsReuseWithoutLocalUploads &&
          profileForSubmission.isReusable &&
          !refreshedProfile.isReusable &&
          refreshedProfile.isAvailable;
        if (canReuseLastKnownProfile) {
          console.warn('[CFDI UI][eFirma] Perfil remoto incompleto; se reutiliza el ultimo perfil local reusable', {
            clientRecordId: selectedClient?.id || '',
            localReuseChecklist: profileForSubmission?.reuseChecklist || null,
            remoteReuseChecklist: refreshedProfile?.reuseChecklist || null,
            remoteReason: refreshedProfile?.reuseStatusReason || ''
          });
        } else {
          profileForSubmission = refreshedProfile;
          setEfirmaProfile(refreshedProfile);
        }
      } catch (refreshError) {
        console.error('[CFDI UI][eFirma] No fue posible refrescar el perfil antes del envio SAT', {
          clientRecordId: selectedClient?.id || '',
          message: refreshError?.message || '',
          status: refreshError?.status || '',
          payload: refreshError?.payload || null
        });
        setSubmitError(
          `No fue posible sincronizar la eFirma antes de enviar la solicitud CFDI. ${refreshError?.message || 'Intenta de nuevo en un momento.'}`
        );
        return;
      }
    }

    const reuseForSubmission = reuseStoredFiles && profileForSubmission.isReusable && !hasLocalCertUploads;
    if (!reuseForSubmission && (!cerInfo.file || !keyInfo.file)) {
      setSubmitError('Debes cargar ambos archivos (.cer y .key).');
      return;
    }
    if (!reuseForSubmission && (cerInfo.error || keyInfo.error)) {
      setSubmitError(cerInfo.error || keyInfo.error);
      return;
    }
    if (!reuseForSubmission && (!isValidSha256Hex(cerInfo.hash) || !isValidSha256Hex(keyInfo.hash))) {
      setSubmitError('No fue posible validar SHA-256 de los certificados. Vuelve a cargar los archivos.');
      return;
    }
    if (!reuseForSubmission && (!cerInfo.base64 || !keyInfo.base64)) {
      setSubmitError('No fue posible preparar los certificados para envio seguro.');
      return;
    }
    if (!String(passphraseRef || '').trim()) {
      setSubmitError('Define passphraseRef antes de enviar la solicitud.');
      return;
    }

    try {
      setLoading(true);
      const satSemantics = buildSatRequestSemantics({
        mode: requestMode,
        requestType
      });
      if (satSemantics.warnings.length > 0) {
        console.warn('[CFDI UI][request] Semantica SAT con advertencias', {
          clientRecordId: selectedClient?.id || '',
          warnings: satSemantics.warnings,
          requestMode: satSemantics.requestMode,
          downloadType: satSemantics.downloadType,
          requestType: satSemantics.requestType
        });
      }
      console.info('[CFDI UI][request] Semantica SAT previa a envio', {
        clientRecordId: selectedClient?.id || '',
        requestMode: satSemantics.requestMode,
        downloadType: satSemantics.downloadType,
        requestType: satSemantics.requestType
      });
      const payload = buildCfdiSecurePayload({
        client: selectedClient,
        mode: requestMode,
        downloadType: satSemantics.downloadType,
        requestType: satSemantics.requestType,
        fechaInicial,
        fechaFinal,
        folio,
        passphraseRef,
        cerFile: reuseForSubmission ? { name: profileForSubmission.cerFileName || 'vigente.cer', size: 1 } : cerInfo.file,
        keyFile: reuseForSubmission ? { name: profileForSubmission.keyFileName || 'vigente.key', size: 1 } : keyInfo.file,
        cerHash: reuseForSubmission ? profileForSubmission.cerSha256 : cerInfo.hash,
        keyHash: reuseForSubmission ? profileForSubmission.keySha256 : keyInfo.hash,
        cerBase64: reuseForSubmission ? '' : cerInfo.base64,
        keyBase64: reuseForSubmission ? '' : keyInfo.base64,
        requestedBy,
        existingEfirma: {
          ...profileForSubmission,
          isReusable: reuseForSubmission
        }
      });

      const response = await sendCfdiSecureRequest({ payload });
      const normalizedStatus = normalizeCfdiRunStatus(response);
      const errorCode = normalizedStatus.errorCode;
      const rawBackendMessage = normalizedStatus.rawErrorMessage || normalizedStatus.errorMessage;
      const errorMessage = resolveBackendErrorMessage(errorCode, rawBackendMessage);
      const submitErrorMessage = buildSubmitErrorMessage({
        errorCode,
        friendlyMessage: errorMessage,
        rawMessage: rawBackendMessage,
        fallbackMessage: 'No fue posible procesar la solicitud CFDI.'
      });
      console.info('[CFDI UI][request] Solicitud procesada', {
        clientRecordId: selectedClient?.id || '',
        requestId: normalizedStatus.requestId,
        idSolicitudSat: normalizedStatus.idSolicitudSat,
        estadoSolicitud: normalizedStatus.estadoSolicitud,
        idsPaquetes: normalizedStatus.idsPaquetes,
        errorCode,
        errorMessage: errorCode ? errorMessage : ''
      });

      setRunStatus({
        requestId: normalizedStatus.requestId,
        clientRecordId: selectedClient?.id || '',
        idSolicitudSat: normalizedStatus.idSolicitudSat,
        requestMode: satSemantics.requestMode,
        requestType: satSemantics.requestType,
        downloadType: satSemantics.downloadType,
        estadoSolicitud: normalizedStatus.estadoSolicitud || 'enviada',
        idsPaquetes: normalizedStatus.idsPaquetes,
        packageHashes: normalizedStatus.packageHashes,
        satBackend: normalizedStatus.satBackend,
        satDataOrigin: normalizedStatus.satDataOrigin,
        isAuthoritative: normalizedStatus.isAuthoritative,
        sourceTrace: normalizedStatus.sourceTrace,
        mirrorInfo: normalizedStatus.mirrorInfo,
        errorCode,
        errorMessage: errorCode ? errorMessage : '',
        rawEstadoSolicitud: normalizedStatus.rawEstadoSolicitud,
        rawErrorMessage: normalizedStatus.rawErrorMessage,
        clientName: normalizedStatus.clientName,
        clientFolderId: normalizedStatus.clientFolderId,
        clientFolderUrl: normalizedStatus.clientFolderUrl,
        metadataFolderId: normalizedStatus.metadataFolderId,
        metadataFolderUrl: normalizedStatus.metadataFolderUrl,
        evidenceFileId: normalizedStatus.evidenceFileId,
        evidenceFileName: normalizedStatus.evidenceFileName,
        evidenceFileUrl: normalizedStatus.evidenceFileUrl,
        evidenceUploaded: normalizedStatus.evidenceUploaded,
        downloadFiles: normalizedStatus.downloadFiles,
        runAt: normalizedStatus.runAt,
        finalizedAt: normalizedStatus.finalizedAt
      });

      const enrichedErrorMessage = normalizedStatus.rawEstadoSolicitud
        ? `${submitErrorMessage} (estado SAT: ${normalizedStatus.rawEstadoSolicitud})`
        : submitErrorMessage;
      if (errorCode) {
        setSubmitError(enrichedErrorMessage);
        setStatusMessage('');
      } else {
        setStatusMessage(buildCfdiUserMessage({ ...normalizedStatus, requestType: satSemantics.requestType }));
      }
    } catch (error) {
      const errorCode = error?.code || error?.errorCode || '';
      const backendMessage = String(
        error?.payload?.errorMessage ||
        error?.payload?.message ||
        error?.message ||
        ''
      ).trim();
      const friendlyMessage = resolveBackendErrorMessage(
        errorCode,
        backendMessage || 'No fue posible enviar la solicitud CFDI.'
      );
      const submitErrorMessage = buildSubmitErrorMessage({
        errorCode,
        friendlyMessage,
        rawMessage: backendMessage,
        fallbackMessage: 'No fue posible enviar la solicitud CFDI.'
      });
      const rawEstadoFromError = String(
        error?.payload?.estadoSolicitud ||
        error?.payload?.status ||
        error?.payload?.rawEstadoSolicitud ||
        ''
      ).trim();
      const rawEstadoSuffix = rawEstadoFromError ? ` (estado SAT: ${rawEstadoFromError})` : '';
      console.error('[CFDI UI][request] Solicitud fallo', {
        clientRecordId: selectedClient?.id || '',
        message: backendMessage || error?.message || '',
        code: errorCode,
        status: error?.status || '',
        payload: error?.payload || null
      });
      setSubmitError(`${submitErrorMessage}${rawEstadoSuffix}`);
    } finally {
      setLoading(false);
    }
  };

  const pollCfdiStatus = useCallback(
    async ({ source = 'manual' } = {}) => {
      const currentRunStatus = runStatusRef.current || {};
      if (!currentRunStatus.requestId) return;
      if (!integrationReady) {
        if (source === 'manual') {
          setSubmitError(integrationConfigError);
        }
        setAutoStatusPollEnabled(false);
        return;
      }
      if (backendBlocked) {
        if (source === 'manual') {
          setSubmitError(`${backendBlockCode}: ${backendBlockMessage || 'Backend SAT real no disponible.'}`);
        }
        setAutoStatusPollEnabled(false);
        return;
      }
      if (statusLoadingRef.current) {
        return;
      }

      setStatusLoading(true);
      if (source === 'manual') {
        setSubmitError('');
      }

      try {
        const requestTypeForStatus = String(currentRunStatus.requestType || requestType || '').trim();
        const downloadTypeForStatus = String(
          currentRunStatus.downloadType ||
          (currentRunStatus.requestMode === 'folio' ? '' : currentRunStatus.requestMode) ||
          ''
        ).trim();

        const response = await fetchCfdiRequestStatus({
          requestId: currentRunStatus.requestId,
          clientRecordId: currentRunStatus.clientRecordId || selectedClientId || '',
          idSolicitud: currentRunStatus.idSolicitudSat || '',
          requestType: requestTypeForStatus,
          downloadType: downloadTypeForStatus
        });
        const normalizedStatus = normalizeCfdiRunStatus(response);
        const errorCode = normalizedStatus.errorCode;
        const errorMessage = resolveBackendErrorMessage(errorCode, normalizedStatus.errorMessage);
        console.info('[CFDI UI][status] Estado consultado', {
          source,
          requestId: currentRunStatus.requestId,
          idSolicitudSat: normalizedStatus.idSolicitudSat,
          estadoSolicitud: normalizedStatus.estadoSolicitud,
          idsPaquetes: normalizedStatus.idsPaquetes,
          errorCode,
          errorMessage: errorCode ? errorMessage : ''
        });

        setRunStatus(prev => ({
          ...prev,
          clientRecordId: prev.clientRecordId || selectedClientId || '',
          idSolicitudSat: normalizedStatus.idSolicitudSat || prev.idSolicitudSat,
          requestMode: prev.requestMode || requestMode,
          requestType: prev.requestType || requestTypeForStatus || requestType,
          downloadType: prev.downloadType || downloadTypeForStatus,
          estadoSolicitud: normalizedStatus.estadoSolicitud || prev.estadoSolicitud,
          idsPaquetes: normalizedStatus.idsPaquetes.length ? normalizedStatus.idsPaquetes : prev.idsPaquetes,
          packageHashes: normalizedStatus.packageHashes.length ? normalizedStatus.packageHashes : prev.packageHashes,
          satBackend: normalizedStatus.satBackend || prev.satBackend,
          satDataOrigin: normalizedStatus.satDataOrigin || prev.satDataOrigin,
          isAuthoritative: normalizedStatus.isAuthoritative,
          sourceTrace: normalizedStatus.sourceTrace || prev.sourceTrace,
          mirrorInfo:
            normalizedStatus.mirrorInfo?.origin || normalizedStatus.mirrorInfo?.mirrorOfSatPackage
              ? normalizedStatus.mirrorInfo
              : prev.mirrorInfo,
          errorCode,
          errorMessage: errorCode ? errorMessage : '',
          clientName: normalizedStatus.clientName || prev.clientName,
          clientFolderId: normalizedStatus.clientFolderId || prev.clientFolderId,
          clientFolderUrl: normalizedStatus.clientFolderUrl || prev.clientFolderUrl,
          metadataFolderId: normalizedStatus.metadataFolderId || prev.metadataFolderId,
          metadataFolderUrl: normalizedStatus.metadataFolderUrl || prev.metadataFolderUrl,
          evidenceFileId: normalizedStatus.evidenceFileId || prev.evidenceFileId,
          evidenceFileName: normalizedStatus.evidenceFileName || prev.evidenceFileName,
          evidenceFileUrl: normalizedStatus.evidenceFileUrl || prev.evidenceFileUrl,
          evidenceUploaded: normalizedStatus.evidenceUploaded || prev.evidenceUploaded,
          downloadFiles: normalizedStatus.downloadFiles.length ? normalizedStatus.downloadFiles : prev.downloadFiles,
          runAt: normalizedStatus.runAt || prev.runAt,
          finalizedAt: normalizedStatus.finalizedAt || prev.finalizedAt,
          rawEstadoSolicitud: normalizedStatus.rawEstadoSolicitud || prev.rawEstadoSolicitud,
          rawErrorMessage: normalizedStatus.rawErrorMessage || prev.rawErrorMessage
        }));
        if (!errorCode) {
          setStatusMessage(
            buildCfdiUserMessage({
              ...normalizedStatus,
              requestType: requestTypeForStatus || requestType,
              downloadFiles: normalizedStatus.downloadFiles.length
                ? normalizedStatus.downloadFiles
                : currentRunStatus.downloadFiles
            })
          );
        }

        const nextStatus = normalizedStatus.estadoSolicitud || currentRunStatus.estadoSolicitud;
        if (errorCode || isTerminalCfdiStatus(nextStatus)) {
          setAutoStatusPollEnabled(false);
        }
      } catch (error) {
        const errorCode = error?.code || error?.errorCode || '';
        const backendMessage = String(
          error?.payload?.errorMessage ||
          error?.payload?.message ||
          error?.message ||
          ''
        ).trim();
        const friendlyMessage = resolveBackendErrorMessage(
          errorCode,
          backendMessage || 'No fue posible consultar el estado.'
        );
        const submitErrorMessage = buildSubmitErrorMessage({
          errorCode,
          friendlyMessage,
          rawMessage: backendMessage,
          fallbackMessage: 'No fue posible consultar el estado.'
        });
        console.error('[CFDI UI][status] Consulta fallo', {
          source,
          requestId: currentRunStatus.requestId,
          message: backendMessage || error?.message || '',
          code: errorCode,
          status: error?.status || '',
          payload: error?.payload || null
        });
        setSubmitError(submitErrorMessage);
        setAutoStatusPollEnabled(false);
      } finally {
        setStatusLoading(false);
      }
    },
    [
      integrationReady,
      integrationConfigError,
      requestMode,
      requestType,
      selectedClientId,
      backendBlocked,
      backendBlockCode,
      backendBlockMessage
    ]
  );

  const handleCheckStatus = async () => {
    await pollCfdiStatus({ source: 'manual' });
  };

  useEffect(() => {
    if (!autoStatusPollEnabled) return;
    const currentRunStatus = runStatusRef.current || {};
    if (!currentRunStatus.requestId || !integrationReady || backendBlocked) {
      setAutoStatusPollEnabled(false);
      return;
    }
    if (isTerminalCfdiStatus(currentRunStatus.estadoSolicitud) || currentRunStatus.errorCode) {
      setAutoStatusPollEnabled(false);
      return;
    }

    const intervalId = setInterval(() => {
      const latest = runStatusRef.current || {};
      if (!latest.requestId || isTerminalCfdiStatus(latest.estadoSolicitud) || latest.errorCode) {
        setAutoStatusPollEnabled(false);
        return;
      }
      if (statusLoadingRef.current) {
        return;
      }
      void pollCfdiStatus({ source: 'auto' });
    }, AUTO_STATUS_POLL_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [autoStatusPollEnabled, integrationReady, backendBlocked, pollCfdiStatus]);

  return (
    <section className="board cfdi-secure-board" data-ui-section="cfdi-secure-board">
      <div className="section-header cfdi-secure-header">
        <div>
          <p className="eyebrow">Extraccion CFDI</p>
          <h3>Solicitud CFDI segura y alineada</h3>
          <p>Coordina eFirma, ejecución y trazabilidad sin exponer secretos ni decorar con métricas.</p>
        </div>
        <div className="cfdi-header-status" aria-live="polite">
          <span className={`cfdi-status-pill tone-${integrationReady ? 'success' : 'danger'}`}>
            {integrationReady ? 'Entorno listo' : 'Entorno incompleto'}
          </span>
          <span className={`cfdi-status-pill tone-${backendBlocked ? 'danger' : signerProbeOnlyBlocked ? 'warning' : backendStatus.checked ? 'success' : 'neutral'}`}>
            {backendBlocked ? 'SAT bloqueado' : signerProbeOnlyBlocked ? 'SAT listo en DEV' : backendStatus.checked ? 'SAT listo' : 'SAT sin preflight'}
          </span>
          <small>
            {integrationReady
              ? backendBlocked
                ? (backendBlockMessage || 'Backend SAT real no disponible.')
                : signerProbeOnlyBlocked
                  ? 'backend=real; el probe estático no está configurado, pero DEV puede continuar con eFirma por solicitud.'
                : backendStatus.checked
                  ? `backend=${backendStatus.backend || 'n/d'}`
                  : 'Define REACT_APP_N8N_CFDI_PREFLIGHT_URL para validar backend SAT.'
              : 'Faltan endpoints CFDI dedicados.'}
          </small>
        </div>
      </div>

      <div className="cfdi-grid">
        <form className="cfdi-form-column" onSubmit={handleSubmit}>
          {!integrationReady && (
            <p className="helper-text error cfdi-feedback-strip" role="alert">{integrationConfigError}</p>
          )}
          {backendBlocked && (
            <p className="helper-text error cfdi-feedback-strip" role="alert">
              {backendBlockCode}: {backendBlockMessage || 'Backend SAT real no disponible.'}
            </p>
          )}
          {efirmaError && <p className="helper-text error cfdi-feedback-strip" role="alert">{efirmaError}</p>}
          {efirmaMessage && <p className="helper-text success cfdi-feedback-strip">{efirmaMessage}</p>}
          {submitError && <p className="helper-text error cfdi-feedback-strip" role="alert">{submitError}</p>}
          {statusMessage && <p className="helper-text success cfdi-feedback-strip">{statusMessage}</p>}

          <section className="cfdi-panel cfdi-panel-credentials">
            <div className="cfdi-panel-head">
              <div>
                <p className="cfdi-kicker">Credenciales / eFirma</p>
                <h4>Identidad, referencia y bloqueo criptografico</h4>
                <p>Los certificados y el passphraseRef definen la huella que vamos a reutilizar.</p>
              </div>
              <div className="cfdi-badge-stack">
                <span className={`cfdi-status-pill tone-${integrationReady ? 'success' : 'danger'}`}>
                  {integrationReady ? 'n8n configurado' : 'falta configuración'}
                </span>
                <span className={`cfdi-status-pill tone-${efirmaTone}`}>
                  {efirmaLoading
                    ? 'Revisando eFirma...'
                    : efirmaStatusReady
                      ? efirmaAvailabilityLabel
                      : 'sin consulta remota'}
                </span>
              </div>
            </div>

            <div className="cfdi-panel-grid">
              <label className="filter-control cfdi-span-full">
                <span>Buscar cliente activo</span>
                <input
                  type="search"
                  value={clientSearch}
                  onChange={event => setClientSearch(event.target.value)}
                  placeholder="Buscar por ID Cliente, nombre, RFC o recordId"
                  autoComplete="off"
                />
              </label>

              <label className="filter-control">
                <span>Cliente</span>
                <select
                  value={selectedClientId}
                  onChange={event => setSelectedClientId(event.target.value)}
                  data-testid="cfdi-client-select"
                >
                  <option value="">Selecciona un cliente</option>
                  {filteredClients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.label || client.id}
                    </option>
                  ))}
                </select>
              </label>

              <label className="filter-control">
                <span>passphraseRef</span>
                <input
                  type="text"
                  value={passphraseRef}
                  onChange={event => setPassphraseRef(event.target.value)}
                  placeholder="vault://sat/clientes/acme"
                  autoComplete="off"
                />
              </label>
            </div>

            {!filteredClients.length && (
              <p className="cfdi-inline-note">No hay clientes activos que coincidan con la búsqueda actual.</p>
            )}

            <div className="cfdi-credential-metadata">
              <div>
                <span>Última carga</span>
                <strong>{formatDateTime(efirmaProfile.lastUploadAt)}</strong>
              </div>
              <div>
                <span>Archivos vigentes</span>
                <strong>
                  {efirmaProfile.cerFileName || efirmaProfile.keyFileName
                    ? [efirmaProfile.cerFileName, efirmaProfile.keyFileName].filter(Boolean).join(' + ')
                    : 'Sin registro'}
                </strong>
                {efirmaProfile.isReusable && (efirmaProfile.cerFileName || efirmaProfile.keyFileName) && (
                  <small>Los archivos vigentes ya estan resguardados para este cliente.</small>
                )}
              </div>
              <div>
                <span>Disponibilidad</span>
                <strong>
                  {efirmaProfile.isReusable
                    ? 'No es necesario volver a cargar archivos para este cliente.'
                    : efirmaProfile.isAvailable
                      ? (efirmaProfile.reuseStatusReason || 'El material existe, pero falta metadata para reuso automatico.')
                      : efirmaStatusReady
                        ? 'Se requiere registrar o actualizar eFirma antes de reutilizarla.'
                        : 'La consulta de vigencia no esta configurada; puedes registrar una eFirma nueva.'}
                </strong>
                {efirmaProfile.drivePath && <small>{efirmaProfile.drivePath}</small>}
                {efirmaProfile.message && !efirmaProfile.isReusable && <small>{efirmaProfile.message}</small>}
                {!efirmaProfile.isReusable &&
                  Array.isArray(efirmaProfile.reuseBlockers) &&
                  efirmaProfile.reuseBlockers.length > 0 && (
                    <small>
                      Motivo de no reuso automatico: {efirmaProfile.reuseBlockers.join(', ')}.
                    </small>
                  )}
                {efirmaProfile.isReusable && efirmaProfile.passphraseRef && (
                  <small>passphraseRef recuperado automaticamente desde el perfil del cliente.</small>
                )}
                {efirmaProfile.isReusable && (
                  <small>
                    Por seguridad del navegador, los archivos .cer y .key no se rellenan de nuevo en pantalla; el
                    sistema reutiliza la version resguardada.
                  </small>
                )}
                {!efirmaStatusReady && efirmaControlReady && (
                  <small>La consulta remota de eFirma aun no esta disponible; el registro manual sigue habilitado.</small>
                )}
              </div>
            </div>
            <div className="cfdi-result-list">
              <p className="cfdi-result-title">Checklist de reuso automatico</p>
              <div className="cfdi-chip-list">
                {efirmaReuseChecklist.map(item => (
                  <span
                    key={item.key}
                    className={`cfdi-chip ${item.ok ? '' : 'is-warning'}`}
                    title={item.value || 'Sin valor'}
                  >
                    {item.label}: {item.ok ? 'ok' : 'faltante'}
                  </span>
                ))}
              </div>
            </div>

            <div className="cfdi-file-grid">
              <label className={`cfdi-file-card tone-${cerHealth.tone}`}>
                <span className="cfdi-file-label">Archivo .cer (max {MAX_FILE_MB} MB)</span>
                <input
                  type="file"
                  accept=".cer,application/pkix-cert"
                  onChange={event => handleCertFile(event.target.files?.[0], '.cer', setCerInfo)}
                />
                <span className={`cfdi-status-pill tone-${cerHealth.tone}`}>{cerHealth.label}</span>
                {cerInfo.file ? (
                  <div className="cfdi-file-meta">
                    <strong>{cerInfo.file.name}</strong>
                    <small>SHA-256: {cerInfo.hash}</small>
                  </div>
                ) : (
                  <p className="cfdi-inline-note">Carga el certificado publico del contribuyente.</p>
                )}
                {cerInfo.error && <small className="helper-text error">{cerInfo.error}</small>}
              </label>

              <label className={`cfdi-file-card tone-${keyHealth.tone}`}>
                <span className="cfdi-file-label">Archivo .key (max {MAX_FILE_MB} MB)</span>
                <input
                  type="file"
                  accept=".key,application/octet-stream"
                  onChange={event => handleCertFile(event.target.files?.[0], '.key', setKeyInfo)}
                />
                <span className={`cfdi-status-pill tone-${keyHealth.tone}`}>{keyHealth.label}</span>
                {keyInfo.file ? (
                  <div className="cfdi-file-meta">
                    <strong>{keyInfo.file.name}</strong>
                    <small>SHA-256: {keyInfo.hash}</small>
                  </div>
                ) : (
                  <p className="cfdi-inline-note">Carga la llave privada asociada al certificado.</p>
                )}
                {keyInfo.error && <small className="helper-text error">{keyInfo.error}</small>}
              </label>
            </div>

            {certsReady && (
              <div className="cfdi-next-steps" aria-live="polite">
                <p className="cfdi-result-title">Siguientes pasos</p>
                <ol className="cfdi-step-list">
                  <li>Verifica que `passphraseRef` corresponda al secreto correcto del cliente.</li>
                  <li>Haz clic en &quot;Registrar / actualizar eFirma&quot; para guardar la referencia reutilizable.</li>
                  <li>Define modo, rango de fechas o folio en la sección de ejecución actual.</li>
                  <li>Envía la solicitud CFDI y luego consulta el estado para seguir el `requestId`.</li>
                </ol>
              </div>
            )}

            <div className="cfdi-actions cfdi-actions-compact">
              <button
                type="button"
                className="btn ghost"
                disabled={efirmaSyncing || !efirmaControlReady}
                onClick={handleSyncEfirma}
              >
                {efirmaSyncing ? 'Registrando eFirma...' : 'Registrar / actualizar eFirma'}
              </button>
              <label className={`cfdi-toggle-card ${shouldReuseExisting ? 'active' : ''}`}>
                <input
                  type="checkbox"
                  checked={reuseStoredFiles}
                  onChange={event => setReuseStoredFiles(event.target.checked)}
                  disabled={!efirmaProfile.isReusable}
                />
                <span>
                  {efirmaProfile.isReusable
                    ? 'Reutilizar automaticamente la eFirma resguardada si sigue vigente.'
                    : 'No hay eFirma vigente reutilizable todavia.'}
                </span>
              </label>
            </div>
          </section>

          <section className="cfdi-panel cfdi-panel-execution">
            <div className="cfdi-panel-head">
              <div>
                <p className="cfdi-kicker">Ejecución actual</p>
                <h4>Solicitud operativa</h4>
                <p>
                  {shouldReuseExisting
                    ? 'Usa la eFirma vigente; solo valida fechas o folio y dispara la corrida.'
                    : 'Define el alcance y asegúrate de cargar archivos cuando haya cambios.'}
                </p>
              </div>
            </div>

            <div className="cfdi-secure-grid cfdi-secure-grid-compact">
              <label className="filter-control">
                <span>Modo</span>
                <select value={requestMode} onChange={event => setRequestMode(event.target.value)}>
                  <option value="emitidos">emitidos</option>
                  <option value="recibidos">recibidos</option>
                  <option value="folio">folio</option>
                </select>
              </label>

              <label className="filter-control">
                <span>Tipo de solicitud</span>
                <select value={requestType} onChange={event => setRequestType(event.target.value)}>
                  <option value="CFDI">CFDI</option>
                  <option value="Metadata">Metadata</option>
                  <option value="PDF">PDF</option>
                  <option value="TXTUUIDMASIVA">TXTUUIDMASIVA</option>
                </select>
                <small className="cfdi-inline-note">
                  {requestType === 'CFDI'
                    ? 'Para CFDI masivo, el entregable principal es uno o varios archivos .zip asociados al paquete SAT.'
                    : `Entregable esperado: ${getRequestDeliverableLabel(requestType)}.`}
                </small>
              </label>

              {requestMode === 'folio' ? (
                <label className="filter-control cfdi-span-full">
                  <span>Folio</span>
                  <input
                    type="text"
                    value={folio}
                    onChange={event => setFolio(event.target.value)}
                    placeholder="UUID o folio exacto"
                  />
                </label>
              ) : (
                <>
                  <label className="filter-control">
                    <span>Fecha inicial</span>
                    <input type="date" value={fechaInicial} onChange={event => setFechaInicial(event.target.value)} />
                  </label>
                  <label className="filter-control">
                    <span>Fecha final</span>
                    <input type="date" value={fechaFinal} onChange={event => setFechaFinal(event.target.value)} />
                  </label>
                </>
              )}
            </div>

            <div className="cfdi-actions cfdi-actions-tight">
              <button
                type="submit"
                className="btn primary"
                disabled={loading || !integrationReady || backendBlocked || efirmaSyncing || efirmaSyncRequired}
              >
                {loading ? 'Enviando...' : 'Enviar solicitud CFDI'}
              </button>
              <button
                type="button"
                className="btn ghost"
                disabled={!canCheckStatus}
                onClick={handleCheckStatus}
              >
                {statusLoading ? 'Consultando...' : 'Consultar estado'}
              </button>
              <button
                type="button"
                className="btn ghost"
                disabled={!canToggleAutoStatusPoll}
                onClick={() => setAutoStatusPollEnabled(previous => !previous)}
              >
                {autoStatusPollEnabled ? 'Auto estado: activo' : 'Auto estado: inactivo'}
              </button>
              {autoStatusPollEnabled && (
                <small className="cfdi-inline-note">
                  Reconsulta automatica cada {Math.round(AUTO_STATUS_POLL_INTERVAL_MS / 1000)}s hasta estado terminal.
                </small>
              )}
              {efirmaSyncRequired && (
                <small className="cfdi-inline-note error">
                  Registra o actualiza la eFirma antes de enviar la solicitud CFDI.
                </small>
              )}
            </div>
          </section>
        </form>

        <section className="cfdi-panel cfdi-panel-history" data-ui-section="cfdi-status">
          <div className="cfdi-panel-head">
            <div>
              <p className="cfdi-kicker">Historial / estado reciente</p>
              <h4>Seguimiento operativo</h4>
              <p>Consulta folio SAT, paquetes, respaldo en Drive y evidencia lista para revisión administrativa.</p>
            </div>
            <div className="cfdi-badge-stack">
              <span className={`cfdi-status-pill tone-${statusTone}`}>
                {statusLabel}
              </span>
              <span className={`cfdi-status-pill tone-${getOriginTone(runStatus)}`}>
                {getOriginLabel(runStatus)}
              </span>
            </div>
          </div>

          <p className="cfdi-inline-note">{buildCfdiSummary(runStatus)}</p>
          {runStatus.rawEstadoSolicitud && (
            <p className="cfdi-inline-note">
              Estado bruto SAT: {runStatus.rawEstadoSolicitud}
              {runStatus.rawErrorMessage ? ` — ${runStatus.rawErrorMessage}` : ''}
            </p>
          )}

          <div className="cfdi-status-grid">
            <div className="cfdi-status-item">
              <span>Folio interno</span>
              <strong>{runStatus.requestId || '—'}</strong>
            </div>
            <div className="cfdi-status-item">
              <span>Folio SAT</span>
              <strong>{runStatus.idSolicitudSat || '—'}</strong>
            </div>
            <div className="cfdi-status-item">
              <span>Resultado</span>
              <strong>{statusLabel}</strong>
            </div>
            <div className="cfdi-status-item">
              <span>Paquetes</span>
              <strong>{runStatus.idsPaquetes.length || 0}</strong>
            </div>
            <div className="cfdi-status-item">
              <span>Cliente</span>
              <strong>{runStatus.clientName || selectedClient?.empresa || '—'}</strong>
            </div>
            <div className="cfdi-status-item">
              <span>Origen del dato</span>
              <strong>{getOriginLabel(runStatus)}</strong>
            </div>
            <div className="cfdi-status-item">
              <span>Backend SAT</span>
              <strong>{runStatus.satBackend || backendStatus.backend || '—'}</strong>
            </div>
            <div className="cfdi-status-item">
              <span>Cierre</span>
              <strong>{formatDateTime(runStatus.finalizedAt || runStatus.runAt)}</strong>
            </div>
          </div>

          <div className="cfdi-result-list">
            {runStatus.requestType === 'CFDI' && runStatus.downloadFiles.length > 0 && (
              <>
                <p className="cfdi-result-title">Archivos principales para descargar</p>
                <p className="cfdi-inline-note">
                  El entregable principal para administrativos son ZIPs asociados al paquete reportado por SAT.
                </p>
                <p className="cfdi-inline-note">
                  Cada archivo ZIP corresponde al resultado operativo del paquete SAT y su espejo interno.
                </p>
                {runStatus.downloadFiles.length === runStatus.idsPaquetes.length && runStatus.idsPaquetes.length > 0 && (
                  <p className="cfdi-inline-note">
                    La cantidad de ZIP coincide con la cantidad de paquetes reportados por SAT.
                  </p>
                )}
                <div className="cfdi-chip-list">
                  {runStatus.downloadFiles.map((file, index) => (
                    <div
                      key={[file.idPaquete, file.fileId, file.fileName, index].filter(Boolean).join('-')}
                      className="cfdi-file-meta"
                    >
                      <strong>{formatPrimaryDownloadLabel(file.fileName)}</strong>
                      <small>{file.idPaquete ? `Paquete SAT: ${file.idPaquete}` : 'Archivo principal listo'}</small>
                      {file.driveUrl ? (
                        <a href={file.driveUrl} target="_blank" rel="noreferrer">
                          Descargar ZIP
                        </a>
                      ) : (
                        <small>Sin enlace directo disponible todavia.</small>
                      )}
                      {file.uploadError && <small>{file.uploadError}</small>}
                    </div>
                  ))}
                </div>
              </>
            )}

            <p className="cfdi-result-title">
              {runStatus.requestType === 'CFDI' && runStatus.downloadFiles.length > 0
                ? 'Paquetes SAT relacionados'
                : 'Paquetes reportados por SAT'}
            </p>
            {runStatus.idsPaquetes.length ? (
              <div className="cfdi-chip-list">
                {runStatus.idsPaquetes.map(packageId => (
                  <span key={packageId} className="cfdi-chip">{packageId}</span>
                ))}
              </div>
            ) : (
              <p className="cfdi-inline-note">Aun no hay paquetes reportados por el flujo.</p>
            )}
          </div>

          {runStatus.packageHashes.length > 0 && (
            <div className="cfdi-result-list">
              <p className="cfdi-result-title">Huella de integridad</p>
              <div className="cfdi-chip-list">
                {runStatus.packageHashes.map((item, index) => (
                  <span
                    key={[item.idPaquete, item.hash, index].filter(Boolean).join('-')}
                    className="cfdi-chip"
                    title={item.hash || ''}
                  >
                    {[item.idPaquete || 'Paquete', getPackageHashLabel(item)].filter(Boolean).join(' · ')}
                  </span>
                ))}
              </div>
            </div>
          )}

          {(runStatus.metadataFolderUrl || runStatus.evidenceFileUrl || runStatus.clientFolderUrl) && (
            <div className="cfdi-result-list">
              <p className="cfdi-result-title">
                {runStatus.requestType === 'CFDI' && runStatus.downloadFiles.length > 0
                  ? 'Evidencia técnica y carpetas de respaldo'
                  : 'Archivos y carpetas útiles'}
              </p>
              <div className="cfdi-chip-list">
                {runStatus.metadataFolderUrl && (
                  <a className="cfdi-chip" href={runStatus.metadataFolderUrl} target="_blank" rel="noreferrer">
                    Carpeta de descarga
                  </a>
                )}
                {runStatus.evidenceFileUrl && (
                  <a className="cfdi-chip" href={runStatus.evidenceFileUrl} target="_blank" rel="noreferrer">
                    {runStatus.evidenceFileName || 'Archivo de evidencia'}
                  </a>
                )}
                {runStatus.clientFolderUrl && (
                  <a className="cfdi-chip" href={runStatus.clientFolderUrl} target="_blank" rel="noreferrer">
                    Expediente del cliente
                  </a>
                )}
              </div>
            </div>
          )}

          {runStatus.errorCode && (
            <p className="helper-text error cfdi-result-alert">
              <strong>{runStatus.errorCode}:</strong> {runStatus.errorMessage}
            </p>
          )}
          {!runStatus.errorCode && !runStatus.requestId && (
            <p className="cfdi-inline-note">Cuando envíes una solicitud, aquí verás el folio SAT, los paquetes y enlaces directos a la carpeta y evidencia.</p>
          )}
        </section>
      </div>
    </section>
  );
}

export default CfdiSecureRequestBoard;
